import React, { useCallback, useMemo } from 'react'
import * as R from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import { useTable, useModal } from 'hooks'
import { toCareer } from 'ducks/router'
import { getColumns, getRowActions } from './model'
import { TAB_NAMES } from '../../../model'
import { candidates } from 'orm'
import { getCurrentAccount } from 'ducks/auth'

const useCandidatesTableQueries = () => {
  const dispatch = useDispatch()

  const handleFetchCandidates = React.useCallback(
    ({ workspaceId, status }, { limit, offset, ordering, search }) => {
      dispatch(
        candidates.fetchStart({
          talent_assessment_workspace: workspaceId,
          status,
          limit,
          offset,
          ordering,
          search,
        }),
      )
    },
    [dispatch],
  )

  return {
    handleFetchCandidates,
  }
}

export const useResultsTable = ({ search }) => {
  const dispatch = useDispatch()
  const columns = useMemo(() => getColumns(), [])
  const rowActions = useMemo(() => getRowActions(), [])
  const [jobSeeker, setJobSeeker] = React.useState(null)

  const status = React.useMemo(() => ['completed', 'started'], [])

  const data = useSelector(candidates.getModel)

  const { count, items, meta } = data ?? {}

  const tableData = React.useMemo(() => R.pipe(R.values)(items), [items])

  const updateQueriesAction = useCallback(
    ({ limit, offset, ordering, search }) => {
      dispatch(
        toCareer(TAB_NAMES.RESULTS, null, {
          limit,
          offset,
          ordering,
          search,
        }),
      )
    },
    [dispatch],
  )

  const account = useSelector(getCurrentAccount)
  const workspaceId = account?.talent_assessment_workspace?.id

  const stableSortingMap = useMemo(
    () => ({ name: 'name', progress: 'status', tested: 'tested' }),
    [],
  )

  const defaultSorting = useMemo(() => [{ id: 'tested', desc: true }], [])

  const { handleFetchCandidates } = useCandidatesTableQueries()

  const fetchAction = React.useCallback(
    ({ limit, offset, ordering, search }) => {
      handleFetchCandidates(
        { workspaceId, status },
        { limit, offset, ordering, search },
      )
    },
    [workspaceId, handleFetchCandidates],
  )

  const {
    state: {
      pagination: { currentPage, pageCount, perPage },
      table: { headers, model, isFetching },
    },
    handlers: {
      pagination: { handlePage, handlePageSize },
    },
  } = useTable({
    columns,
    data: tableData,
    mapSortingPropToQueryParam: stableSortingMap,
    defaultSorting,
    pageCount: count,
    search,
    fetchAction,
    updateQueriesAction,
  })

  const { handleClose, handleOpen, isOpen } = useModal()

  const handleOpenModal = jobSeeker => {
    setJobSeeker(jobSeeker)
    handleOpen()
  }

  return {
    state: {
      pagination: { currentPage, pageCount, perPage },
      table: {
        headers,
        model,
        rowActions,
        isFetching,
      },
      isOpen,
      jobSeeker,
    },
    handlers: {
      pagination: { handlePage, handlePageSize },
      handleClose,
      handleOpenModal,
    },
  }
}
